import { z } from "zod";
import { Id, IsoDateTime, Page, PageQuery } from "./common";
import { RiskLevel } from "./state";

export const WorkItemStatus = z.enum([
  "Open",
  "InProgress",
  "OnHold",
  "Resolved",
  "Closed",
]);
export type WorkItemStatus = z.infer<typeof WorkItemStatus>;

export const WorkItemPriority = z.enum(["Critical", "High", "Medium", "Low"]);
export type WorkItemPriority = z.infer<typeof WorkItemPriority>;

export const WorkItemSource = z.enum(["Alert", "Manual", "Import", "System"]);
export type WorkItemSource = z.infer<typeof WorkItemSource>;

export const WorkItem = z.object({
  workItemId: Id,
  tenantId: Id,
  title: z.string().min(1).max(160),
  description: z.string().max(4000).nullable(),
  status: WorkItemStatus,
  priority: WorkItemPriority,
  source: WorkItemSource,
  siteId: Id.nullable(),
  buildingId: Id.nullable(),
  floorId: Id.nullable(),
  assetId: Id.nullable(),
  deviceId: Id.nullable(),
  alertId: Id.nullable(),
  assignedUserId: Id.nullable(),
  riskLevelAtCreation: RiskLevel.nullable(),
  resolutionNote: z.string().max(2000).nullable(),
  closedAt: IsoDateTime.nullable(),
  closedBy: Id.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type WorkItem = z.infer<typeof WorkItem>;

export const WorkItemStatusChangeEvent = z.object({
  workItemStatusChangeEventId: Id,
  tenantId: Id,
  workItemId: Id,
  fromStatus: WorkItemStatus.nullable(),
  toStatus: WorkItemStatus,
  changedAt: IsoDateTime,
  changedBy: Id.nullable(),
  note: z.string().max(2000).nullable(),
});
export type WorkItemStatusChangeEvent = z.infer<
  typeof WorkItemStatusChangeEvent
>;

export const WorkItemListFilters = PageQuery.extend({
  status: WorkItemStatus.optional(),
  priority: WorkItemPriority.optional(),
  source: WorkItemSource.optional(),
  siteId: Id.optional(),
  buildingId: Id.optional(),
  floorId: Id.optional(),
  assetId: Id.optional(),
  deviceId: Id.optional(),
  alertId: Id.optional(),
  assignedUserId: Id.optional(),
  riskLevel: RiskLevel.optional(),
});
export type WorkItemListFilters = z.infer<typeof WorkItemListFilters>;

export const WorkItemCreateInput = z.object({
  title: z.string().min(1).max(160),
  description: z.string().max(4000).nullable().optional(),
  priority: WorkItemPriority.default("Medium"),
  source: WorkItemSource.default("Manual"),
  siteId: Id.nullable().optional(),
  buildingId: Id.nullable().optional(),
  floorId: Id.nullable().optional(),
  assetId: Id.nullable().optional(),
  deviceId: Id.nullable().optional(),
  alertId: Id.nullable().optional(),
  assignedUserId: Id.nullable().optional(),
});
export type WorkItemCreateInput = z.infer<typeof WorkItemCreateInput>;

export const WorkItemUpdateInput = z
  .object({
    title: z.string().min(1).max(160),
    description: z.string().max(4000).nullable(),
    priority: WorkItemPriority,
    siteId: Id.nullable(),
    buildingId: Id.nullable(),
    floorId: Id.nullable(),
    assetId: Id.nullable(),
    deviceId: Id.nullable(),
    alertId: Id.nullable(),
  })
  .partial();
export type WorkItemUpdateInput = z.infer<typeof WorkItemUpdateInput>;

export const WorkItemMoveInput = z.object({
  status: WorkItemStatus,
  note: z.string().max(2000).optional(),
  /**
   * Required when status === "Closed". Makes board drag-to-close semantically
   * identical to POST .../close — both paths produce a resolutionNote on the item.
   */
  resolutionNote: z.string().min(1).max(2000).optional(),
});
export type WorkItemMoveInput = z.infer<typeof WorkItemMoveInput>;

export const WorkItemAssignInput = z.object({
  assignedUserId: Id.nullable(),
});
export type WorkItemAssignInput = z.infer<typeof WorkItemAssignInput>;

export const WorkItemCloseInput = z.object({
  resolutionNote: z.string().min(1).max(2000),
});
export type WorkItemCloseInput = z.infer<typeof WorkItemCloseInput>;

export const WorkItemListResponse = Page(WorkItem);
export type WorkItemListResponse = z.infer<typeof WorkItemListResponse>;

export const WorkBoardColumn = z.object({
  status: WorkItemStatus,
  items: z.array(WorkItem),
  total: z.number().int().min(0),
});
export type WorkBoardColumn = z.infer<typeof WorkBoardColumn>;

export const WorkBoardResponse = z.object({
  tenantId: Id,
  generatedAt: IsoDateTime,
  columns: z.array(WorkBoardColumn),
});
export type WorkBoardResponse = z.infer<typeof WorkBoardResponse>;
